import { useCallback } from 'react';
import { ScopeT, SelectionBaseT } from 'business/definitions';
import { USER_CREATION_FORM_ACTION_TYPE } from '../../../CreateUserContext/types';
import { useUserCreationFormDispatchContext } from '../../../CreateUserContext/CreateUserFormContext';

export type BlockValuesUpdater = (
    blockValues: SelectionBaseT[],
    blockName: string,
) => void;

export type BlockValueRemover = (
    blockValue: SelectionBaseT,
    blockName: string,
) => void;

/**
 * Returns an helper to perform update actions on scope or writeScope block values.
 * @param {ScopeT} scope - The scope configuration
 * @param {string} blockName - The name of the restriction block
 * @param {string} actionType - The specific action type to dispatch
 * @returns {(blockValues: SelectionBaseT[] | SelectionBaseT) => void}
 */
const useBlockValuesUpdateAction = (
    scope: ScopeT,
    actionType: 'UPDATE_SCOPE_BLOCK_VALUES' | 'UPDATE_WRITE_SCOPE_BLOCK_VALUES',
): BlockValuesUpdater => {
    const { dispatchUserCreationForm } = useUserCreationFormDispatchContext();

    return useCallback(
        (blockValues: SelectionBaseT[], blockName: string) => {
            dispatchUserCreationForm({
                type: actionType,
                scope,
                blockName,
                blockValues,
            });
        },
        [dispatchUserCreationForm, scope, actionType],
    );
};

/**
 * Returns a helper to perform remove actions on scope or writeScope block values.
 * @param {ScopeT} scope - The scope configuration
 * @param {string} blockName - The name of the restriction block
 * @param {string} actionType - The specific action type to dispatch
 * @returns {(blockValues: SelectionBaseT[] | SelectionBaseT) => void}
 */
const useBlockValueRemoveAction = (
    scope: ScopeT,
    actionType:
        | 'REMOVE_BLOCK_VALUE_FROM_SCOPE'
        | 'REMOVE_BLOCK_VALUE_FROM_WRITE_SCOPE',
): BlockValueRemover => {
    const { dispatchUserCreationForm } = useUserCreationFormDispatchContext();

    return useCallback(
        (blockValue: SelectionBaseT, blockName: string) => {
            dispatchUserCreationForm({
                type: actionType,
                scope,
                blockName,
                blockValue,
            });
        },
        [dispatchUserCreationForm, scope, actionType],
    );
};

export const useGetScopeBlockValuesUpdater = (
    scope: ScopeT,
): BlockValuesUpdater =>
    useBlockValuesUpdateAction(
        scope,
        USER_CREATION_FORM_ACTION_TYPE.updateScopeBlockValues,
    );

export const useGetScopeBlockValueRemover = (
    scope: ScopeT,
): BlockValueRemover =>
    useBlockValueRemoveAction(
        scope,
        USER_CREATION_FORM_ACTION_TYPE.removeBlockValueFromScope,
    );

export const useGetWriteScopeBlockValuesUpdater = (
    scope: ScopeT,
): BlockValuesUpdater =>
    useBlockValuesUpdateAction(
        scope,
        USER_CREATION_FORM_ACTION_TYPE.updateWriteScopeBlockValues,
    );

export const useGetWriteScopeBlockValueRemover = (
    scope: ScopeT,
): BlockValueRemover =>
    useBlockValueRemoveAction(
        scope,
        USER_CREATION_FORM_ACTION_TYPE.removeBlockValueFromWriteScope,
    );
